import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { useState } from "react";
import { useGetList } from "react-admin";
import { AddAutoFormDocumentDialog } from "../../dialogs/AddAutoFormDocumentDialog";
import { CreateAusbildungsnachweisDialog } from "../../dialogs/CreateAusbildungsnachweisDialog";
import { CreateSchaltnachweisDialog } from "../../dialogs/CreateSchaltnachweisDialog";
import { CreateTeilnahmebescheinigungDialog } from "../../dialogs/CreateTeilnahmebescheinigungDialog";
import { GenerateContractPdfDialog } from "../../dialogs/GenerateContractPdfDialog";
import { useDialog } from "../../hooks/useDialog";
import { useMenu } from "../../hooks/useMenu";
import { AddDocumentIcon } from "../../icons/AddDocumentIcon";
import { FactCheckIcon } from "../../icons/FactCheckIcon";
import { ManualGearIcon } from "../../icons/ManualGearIcon";
import { MotorcycleIcon } from "../../icons/MotorcycleIcon";
import { TrailerIcon } from "../../icons/TrailerIcon";
import { grants } from "../../backoffice.access_control";
import { type DrivingSchoolAutoForm } from "../../providers/autoFormsProvider";
import { Student } from "../../providers/studentsProvider";

export function CreateDocumentMenu({ student }: { student: Student }) {
  const { anchorEl, open, openMenu, closeMenu } = useMenu();
  const { openDialog: openAusbildungsnachweisDialog, dialogProps: ausbildungsnachweisDialogProps } = useDialog(
    "Ausbildungsnachweis erstellen",
  );
  const { openDialog: openSchaltnachweisDialog, dialogProps: schaltnachweisDialogProps } =
    useDialog("Schaltnachweis erstellen");
  const { openDialog: openGenerateContractPdfDialog, dialogProps: generateContractPdfDialogProps } =
    useDialog("Ausbildungsvertrag erstellen");
  const { openDialog: openB196TeilnahmebescheinigungDialog, dialogProps: b196TeilnahmebescheinigungDialogProps } =
    useDialog("B196 Teilnahmebescheinigung erstellen");
  const { openDialog: openB96TeilnahmebescheinigungDialog, dialogProps: b96TeilnahmebescheinigungDialogProps } =
    useDialog("B96 Teilnahmebescheinigung erstellen");
  const { openDialog: openASFTeilnahmebescheinigungDialog, dialogProps: asfTeilnahmebescheinigungDialogProps } =
    useDialog("ASF Teilnahmebescheinigung erstellen");
  const [selectedAutoForm, setSelectedAutoForm] = useState<DrivingSchoolAutoForm | null>(null);
  const { openDialog: openAutoFormDialog, dialogProps: autoFormDialogProps } = useDialog();
  const { data: autoForms = [] } = useGetList<DrivingSchoolAutoForm>("autoForms", {
    filter: { drivingSchoolId: student.drivingSchoolId },
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });
  const validAutoForms = autoForms.filter((form) => form.pdfTemplate && form.pdfTemplateBaseFile);
  const menuItems: Array<React.ReactNode> = [];
  const ausbildungsnachweisCanBeCreated = student.bookedTrainings.some((it) => it.isForDrivingLicense);
  const b196TeilnahmebescheinigungCanBeCreated = student.bookedTrainings.some(
    (it) => it.drivingLicenseClass === "B196",
  );
  const b96TeilnahmebescheinigungCanBeCreated = student.bookedTrainings.some((it) => it.drivingLicenseClass === "B96");
  const asfCourseId = student.bookedTrainings.find((it) => it.asfCourseId)?.asfCourseId;
  const contractPdfCanBeGenerated =
    grants.has("Contract:generatePdf") && student.bookedTrainings.some((bookedTraining) => !bookedTraining.isCancelled);
  if (ausbildungsnachweisCanBeCreated) {
    menuItems.push(
      <MenuItem
        key="ausbildungsnachweis"
        onClick={() => {
          closeMenu();
          openAusbildungsnachweisDialog();
        }}
      >
        <ListItemIcon>
          <FactCheckIcon sx={{ fontSize: 20 }} />
        </ListItemIcon>
        <ListItemText>Ausbildungsnachweis erstellen</ListItemText>
      </MenuItem>,
    );
  }
  if (contractPdfCanBeGenerated) {
    menuItems.push(
      <MenuItem
        key="recreate-contract"
        onClick={() => {
          closeMenu();
          openGenerateContractPdfDialog();
        }}
      >
        <ListItemIcon>
          <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />
        </ListItemIcon>
        <ListItemText>Vertragsdokument erstellen (PDF)</ListItemText>
      </MenuItem>,
    );
  }
  if (b196TeilnahmebescheinigungCanBeCreated) {
    menuItems.push(
      <MenuItem
        key="b196_teilnahmebescheinigung"
        onClick={() => {
          closeMenu();
          openB196TeilnahmebescheinigungDialog();
        }}
      >
        <ListItemIcon>
          <MotorcycleIcon sx={{ fontSize: 20 }} />
        </ListItemIcon>
        <ListItemText>B196 Teilnahmebescheinigung erstellen</ListItemText>
      </MenuItem>,
    );
  }
  const schaltnachweisCanBeCreated = student.bookedTrainings.some((it) => it.drivingLicenseClass === "B197");
  if (schaltnachweisCanBeCreated) {
    menuItems.push(
      <MenuItem
        key="schaltnachweis"
        onClick={() => {
          closeMenu();
          openSchaltnachweisDialog();
        }}
      >
        <ListItemIcon>
          <ManualGearIcon sx={{ fontSize: 20 }} />
        </ListItemIcon>
        <ListItemText>Schaltnachweis erstellen</ListItemText>
      </MenuItem>,
    );
  }
  if (b96TeilnahmebescheinigungCanBeCreated) {
    menuItems.push(
      <MenuItem
        key="b96_teilnahmebescheinigung"
        onClick={() => {
          closeMenu();
          openB96TeilnahmebescheinigungDialog();
        }}
      >
        <ListItemIcon>
          <TrailerIcon sx={{ fontSize: 20 }} />
        </ListItemIcon>
        <ListItemText>B96 Teilnahmebescheinigung erstellen</ListItemText>
      </MenuItem>,
    );
  }
  if (asfCourseId) {
    menuItems.push(
      <MenuItem
        key="asf_teilnahmebescheinigung"
        onClick={() => {
          closeMenu();
          openASFTeilnahmebescheinigungDialog();
        }}
      >
        <ListItemIcon>
          <TrailerIcon sx={{ fontSize: 20 }} />
        </ListItemIcon>
        <ListItemText>ASF Teilnahmebescheinigung erstellen</ListItemText>
      </MenuItem>,
    );
  }

  for (const autoForm of validAutoForms) {
    menuItems.push(
      <MenuItem
        key={`autoForm-${autoForm.id}`}
        onClick={() => {
          setSelectedAutoForm(autoForm);
          closeMenu();
          openAutoFormDialog();
        }}
      >
        <ListItemIcon>
          <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />
        </ListItemIcon>
        <ListItemText>{`${autoForm.name} erstellen`}</ListItemText>
      </MenuItem>,
    );
  }

  if (menuItems.length === 0) {
    return null;
  }

  return (
    <>
      <Tooltip title="Dokumente erstellen">
        <IconButton onClick={openMenu} data-testid="CreateDocumentMenu-IconButton">
          <AddDocumentIcon />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
        {menuItems}
      </Menu>
      {ausbildungsnachweisCanBeCreated && (
        <CreateAusbildungsnachweisDialog student={student} {...ausbildungsnachweisDialogProps} />
      )}
      {b196TeilnahmebescheinigungCanBeCreated && (
        <CreateTeilnahmebescheinigungDialog
          student={student}
          type="b196_teilnahmebescheinigung"
          {...b196TeilnahmebescheinigungDialogProps}
        />
      )}
      {b96TeilnahmebescheinigungCanBeCreated && (
        <CreateTeilnahmebescheinigungDialog
          student={student}
          type="b96_teilnahmebescheinigung"
          {...b96TeilnahmebescheinigungDialogProps}
        />
      )}
      {asfCourseId && (
        <CreateTeilnahmebescheinigungDialog
          student={student}
          type="asf_teilnahmebescheinigung"
          asfCourseId={asfCourseId}
          {...asfTeilnahmebescheinigungDialogProps}
        />
      )}
      {schaltnachweisCanBeCreated && <CreateSchaltnachweisDialog student={student} {...schaltnachweisDialogProps} />}
      {contractPdfCanBeGenerated && <GenerateContractPdfDialog student={student} {...generateContractPdfDialogProps} />}
      {selectedAutoForm && (
        <AddAutoFormDocumentDialog student={student} autoForm={selectedAutoForm} {...autoFormDialogProps} />
      )}
    </>
  );
}
